import { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import api from "@/api/api.js";

/**
 * El contador da por atendida una asesoría o una consulta del emisor.
 *
 * El botón de WhatsApp del usuario no descuenta nada: abrir una conversación no
 * es haber atendido. Se descuenta aquí, al terminar, y con una nota de qué fue,
 * porque el usuario paga por esa atención y puede reclamarla.
 *
 * Cuánto cupo le queda lo dice el servidor al registrar. Si no le queda, la
 * respuesta lo explica y no se descuenta nada.
 */
const RegisterQuotaButtons = ({ userId, onRegistered }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const register = async (type) => {
    if (busy || !userId) return;

    const { value: note, isConfirmed } = await Swal.fire({
      title: t(`ui.quota.register.${type}`),
      input: "textarea",
      inputLabel: t("ui.quota.register.noteLabel"),
      inputPlaceholder: t("ui.quota.register.notePlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("ui.quota.register.confirm"),
      cancelButtonText: t("ui.cancel"),
    });

    if (!isConfirmed) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/users/${userId}/quota-consumptions`, {
        type,
        note,
      });

      const left = data?.quotas?.[type]?.available ?? 0;
      await Swal.fire({
        icon: "success",
        title: t("ui.quota.register.done"),
        text: t("ui.quota.remaining", { count: left }),
      });

      onRegistered?.(data);
    } catch (error) {
      await Swal.fire({
        icon: "warning",
        title: t("ui.quota.register.notDone"),
        text:
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          t("ui.quota.register.notDoneHint"),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6 className="card-title">{t("ui.quota.register.title")}</h6>
        <p className="text-muted small mb-3">{t("ui.quota.register.hint")}</p>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            disabled={busy}
            onClick={() => register("advisories")}
          >
            <i className="bi bi-headset me-1" />
            {t("ui.quota.register.advisories")}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={busy}
            onClick={() => register("consultations")}
          >
            <i className="bi bi-question-circle me-1" />
            {t("ui.quota.register.consultations")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegisterQuotaButtons;
